import type { ReactNode } from 'react';
import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Pressable, Stack, xcss } from '@atlaskit/primitives';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

const i18n = defineMessages({
	copyLinkCTA: {
		id: 'editor-features.user.copy-your-page',
		defaultMessage: 'Copy link',
		description:
			'This text is shown on successfully published flag to indicate that they can click this text to copy the URL to the current content (page/blog/etc...)',
	},
	copiedCTA: {
		id: 'editor-features.user.copied-page',
		defaultMessage: 'Copied',
		description:
			'This text is shown when a user has clicked a CTA and has already copied the URL to the current page',
	},
});

const linkStyles = xcss({
	font: token('font.body'),
	color: 'color.link',
	fontWeight: token('font.weight.medium'),
	padding: 'space.0',
	paddingRight: 'space.025',
	cursor: 'pointer',
	':hover': {
		textDecoration: 'underline',
	},
	':focus-visible': {
		outlineStyle: 'auto',
		// @ts-expect-error - outlineOffset currently doesn't expect negative values
		outlineOffset: '-1px',
	},
});

const copiedLinkStyles = xcss({
	color: 'color.text.subtle',
	cursor: 'default',
	':hover': {
		textDecoration: 'none',
	},
	':focus-visible': {
		outlineStyle: 'none',
	},
});

export const CopyLinkFlagComponent = ({
	successPublishMessage,
	copyContentLink,
	createAnalyticsEvent,
	isFirstTimePublish,
}: {
	successPublishMessage: ReactNode;
	copyContentLink: () => void;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	isFirstTimePublish: boolean;
}) => {
	const [isLinkCopied, setLinkCopied] = useState<boolean>(false);

	useEffect(() => {
		if (isLinkCopied) {
			const timeout = setTimeout(() => {
				setLinkCopied(false);
			}, 4000);

			return () => clearTimeout(timeout);
		}
	}, [isLinkCopied]);

	const handleClick = useCallback(() => {
		if (isLinkCopied) return;
		setLinkCopied(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'publishFlagCopyPageCTA',
				source: 'publishSuccessFlag',
				attributes: {
					isFirstTimePublish,
				},
			},
		}).fire();
		void copyContentLink();
	}, [isLinkCopied, setLinkCopied, copyContentLink, createAnalyticsEvent, isFirstTimePublish]);

	return (
		<Stack space="space.100" alignInline="start">
			<span>{successPublishMessage}</span>
			<Pressable
				onClick={handleClick}
				backgroundColor="color.background.neutral.subtle"
				xcss={[linkStyles, isLinkCopied && copiedLinkStyles]}
			>
				<FormattedMessage {...(isLinkCopied ? i18n.copiedCTA : i18n.copyLinkCTA)} />
			</Pressable>
		</Stack>
	);
};
